import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Button, Input, Upload, Progress, Switch, message } from 'antd';
import { CheckCircle, AlertCircle, X, Sparkles, FileText, Layers, Mic, StickyNote } from 'lucide-react';
import {
  importPptx,
  importGoogleSlides,
  getImportStatus,
  getStatusMessage,
  ImportJobStatus,
} from '../../services/presentationImportService';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import { UpgradePrompt } from '../overlays/UpgradePrompt';
import './PresentationImportModal.scss';

interface PresentationImportModalProps {
  open: boolean;
  onClose: () => void;
}

export const PresentationImportModal: React.FC<PresentationImportModalProps> = ({
  open,
  onClose,
}) => {
  const navigate = useNavigate();
  const { isPaid } = useFeatureAccess();
  const [activeTab, setActiveTab] = useState<string>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [googleUrl, setGoogleUrl] = useState('');
  const [projectName, setProjectName] = useState('');
  const [importSpeakerNotes, setImportSpeakerNotes] = useState(true);
  const [generateAIScripts, setGenerateAIScripts] = useState(false);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [importing, setImporting] = useState(false);
  const [jobStatus, setJobStatus] = useState<ImportJobStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const cleanup = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  const resetState = useCallback(() => {
    setFile(null);
    setGoogleUrl('');
    setProjectName('');
    setImportSpeakerNotes(true);
    setGenerateAIScripts(false);
    setImporting(false);
    setJobStatus(null);
    setError(null);
    cleanup();
  }, [cleanup]);

  const handleClose = useCallback(() => {
    if (importing && jobStatus?.status !== 'completed' && jobStatus?.status !== 'failed') {
      return;
    }
    resetState();
    onClose();
  }, [importing, jobStatus, resetState, onClose]);

  const startPolling = useCallback((jobId: string) => {
    cleanup();
    pollRef.current = setInterval(async () => {
      try {
        const status = await getImportStatus(jobId);
        setJobStatus(status);

        if (status.status === 'completed') {
          cleanup();
          message.success('Presentation imported successfully!');
          setTimeout(() => {
            resetState();
            onClose();
            if (status.projectId) {
              navigate(`/recorder?mode=project&projectId=${status.projectId}`);
            }
          }, 1000);
        } else if (status.status === 'failed') {
          cleanup();
          setError(status.error || 'Import failed unexpectedly');
          setImporting(false);
        }
      } catch (err) {
        console.error('[PresentationImport] Polling error:', err);
      }
    }, 2000);
  }, [cleanup, resetState, onClose, navigate]);

  const handleUploadPptx = useCallback(async () => {
    if (!file) {
      message.warning('Please select a .pptx file first');
      return;
    }

    setImporting(true);
    setError(null);
    setJobStatus({
      jobId: '',
      status: 'extracting',
      progress: 0,
      totalSlides: 0,
      processedSlides: 0,
      projectId: null,
      error: null,
    });

    try {
      const name = projectName.trim() || file.name.replace(/\.pptx$/i, '');
      const result = await importPptx(file, name, undefined, {
        importSpeakerNotes,
        generateAIScripts: isPaid ? generateAIScripts : false,
      });
      startPolling(result.jobId);
      setJobStatus({
        jobId: result.jobId,
        status: 'extracting',
        progress: 5,
        totalSlides: 0,
        processedSlides: 0,
        projectId: null,
        error: null,
      });
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to start import');
      setImporting(false);
    }
  }, [file, projectName, startPolling, importSpeakerNotes, generateAIScripts, isPaid]);

  const handleImportGoogleSlides = useCallback(async () => {
    if (!googleUrl.trim()) {
      message.warning('Please paste a Google Slides link');
      return;
    }

    setImporting(true);
    setError(null);
    setJobStatus({
      jobId: '',
      status: 'extracting',
      progress: 0,
      totalSlides: 0,
      processedSlides: 0,
      projectId: null,
      error: null,
    });

    try {
      const result = await importGoogleSlides(googleUrl.trim(), projectName.trim() || undefined, undefined, {
        importSpeakerNotes,
        generateAIScripts: isPaid ? generateAIScripts : false,
      });
      startPolling(result.jobId);
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || err.message || 'Failed to import';
      const hint = err.response?.data?.hint;
      setError(hint ? `${errorMsg}\n\n${hint}` : errorMsg);
      setImporting(false);
    }
  }, [googleUrl, projectName, startPolling, importSpeakerNotes, generateAIScripts, isPaid]);

  const handleFileDrop = useCallback((info: any) => {
    const droppedFile = info.file?.originFileObj || info.file;
    if (droppedFile) {
      const ext = droppedFile.name.toLowerCase();
      if (!ext.endsWith('.pptx')) {
        message.error('Only .pptx files are supported');
        return;
      }
      if (droppedFile.size > 100 * 1024 * 1024) {
        message.error('File size must be under 100MB');
        return;
      }
      setFile(droppedFile);
      setError(null);
      if (!projectName.trim()) {
        setProjectName(droppedFile.name.replace(/\.pptx$/i, ''));
      }
    }
  }, [projectName]);

  const isGoogleUrlValid = /\/presentation\/d\/[a-zA-Z0-9_-]+/.test(googleUrl);

  const renderUploadContent = () => (
    <div className="import-form-area">
      <Upload.Dragger
        accept=".pptx"
        showUploadList={false}
        beforeUpload={() => false}
        onChange={handleFileDrop}
        disabled={importing}
        className="pptx-upload-dragger"
      >
        {file ? (
          <div className="file-selected">
            <img src="/images/ppt-icon.svg" alt="PPT" className="file-type-icon" />
            <div className="file-info">
              <p className="file-name">{file.name}</p>
              <p className="file-size">{(file.size / (1024 * 1024)).toFixed(1)} MB</p>
            </div>
            {!importing && (
              <button
                className="file-remove-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setFile(null);
                }}
              >
                <X size={14} />
              </button>
            )}
          </div>
        ) : (
          <div className="upload-placeholder">
            <img src="/images/ppt-icon.svg" alt="PPT" className="upload-icon-img" />
            <p className="upload-text">Drag & drop your .pptx file here</p>
            <p className="upload-hint">or click to browse (max 100MB)</p>
          </div>
        )}
      </Upload.Dragger>

      <div className="import-field">
        <label>Project Name</label>
        <Input
          value={projectName}
          onChange={(e) => setProjectName(e.target.value)}
          placeholder="Defaults to file name"
          disabled={importing}
          maxLength={150}
        />
      </div>

      {!importing && !jobStatus && (
        <Button
          type="primary"
          block
          size="large"
          onClick={handleUploadPptx}
          disabled={!file}
          className="import-action-btn"
        >
          Import Presentation
        </Button>
      )}
    </div>
  );

  const renderGoogleSlidesContent = () => (
    <div className="import-form-area">
      <div className="google-slides-share-info">
        <p className="share-hint">Make sure your presentation link is set to "Anyone with the link" before importing.</p>
        <p className="share-hint">In Google Slides: Share → General access → Anyone with the link</p>
      </div>

      <div className="import-field">
        <label>Google Slides Link</label>
        <Input
          value={googleUrl}
          onChange={(e) => setGoogleUrl(e.target.value)}
          placeholder="https://docs.google.com/presentation/d/..."
          disabled={importing}
          status={googleUrl && !isGoogleUrlValid ? 'error' : undefined}
        />
        {googleUrl && !isGoogleUrlValid && (
          <p className="field-error">Please enter a valid Google Slides URL</p>
        )}
      </div>

      <div className="import-field">
        <label>Project Name</label>
        <Input
          value={projectName}
          onChange={(e) => setProjectName(e.target.value)}
          placeholder="Defaults to presentation title"
          disabled={importing}
          maxLength={150}
        />
      </div>

      {!importing && !jobStatus && (
        <Button
          type="primary"
          block
          size="large"
          onClick={handleImportGoogleSlides}
          disabled={!isGoogleUrlValid}
          className="import-action-btn"
        >
          Import from Google Slides
        </Button>
      )}
    </div>
  );

  const renderProgress = () => {
    if (!jobStatus) return null;

    const isComplete = jobStatus.status === 'completed';
    const isFailed = jobStatus.status === 'failed';

    return (
      <div className="import-progress-area">
        <div className="progress-visual">
          {isComplete ? (
            <div className="progress-icon success">
              <CheckCircle size={32} />
            </div>
          ) : isFailed ? (
            <div className="progress-icon failed">
              <AlertCircle size={32} />
            </div>
          ) : (
            <div className="progress-icon processing">
              <img src="/images/slides-icon.svg" alt="Processing" className="processing-icon-img" />
            </div>
          )}
        </div>

        <p className="progress-label">{getStatusMessage(jobStatus.status)}</p>

        {!isFailed && (
          <div className="progress-bar-wrapper">
            <Progress
              percent={jobStatus.progress}
              status={isComplete ? 'success' : 'active'}
              strokeColor={isComplete ? '#10B981' : '#4A3CFA'}
              trailColor="#E5E7EB"
              showInfo={false}
              size={{ height: 6 }}
            />
            <span className="progress-percent">{jobStatus.progress}%</span>
          </div>
        )}

        {jobStatus.totalSlides > 0 && !isComplete && !isFailed && (
          <p className="slide-progress">
            Processing {jobStatus.processedSlides} of {jobStatus.totalSlides} slides
          </p>
        )}
      </div>
    );
  };

  const features = [
    { icon: Layers, label: 'Each slide becomes a scene' },
    { icon: Sparkles, label: 'AI generates narration scripts' },
    { icon: Mic, label: 'Record with mic, webcam, or both' },
  ];

  return (
    <>
    <Modal
      open={open}
      onCancel={handleClose}
      footer={null}
      width={560}
      destroyOnHidden
      maskClosable={!importing}
      closable={!importing || jobStatus?.status === 'completed' || jobStatus?.status === 'failed'}
      className="presentation-import-modal"
    >
      <div className="modal-header-section">
        <div className="header-icon-wrapper">
          <FileText size={20} color="#4A3CFA" />
        </div>
        <div className="header-text">
          <h3>Import Presentation</h3>
          <p>Turn your slides into video scenes with AI-generated scripts</p>
        </div>
      </div>

      {importing || jobStatus ? (
        <div className="import-processing-section">
          {renderProgress()}
          {error && (
            <div className="import-error">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}
          {(jobStatus?.status === 'failed') && (
            <Button
              type="primary"
              onClick={resetState}
              className="retry-btn"
            >
              Try Again
            </Button>
          )}
        </div>
      ) : (
        <>
          <div className="features-strip">
            {features.map((f, i) => (
              <div key={i} className="feature-item">
                <f.icon size={14} />
                <span>{f.label}</span>
              </div>
            ))}
          </div>

          <div className="source-tabs">
            <button
              className={`source-tab ${activeTab === 'upload' ? 'active' : ''}`}
              onClick={() => setActiveTab('upload')}
            >
              <img src="/images/ppt-icon.svg" alt="PPT" className="tab-icon-img" />
              Upload .pptx
            </button>
            <button
              className={`source-tab ${activeTab === 'google' ? 'active' : ''}`}
              onClick={() => setActiveTab('google')}
            >
              <img src="/images/slides-icon.svg" alt="Slides" className="tab-icon-img" />
              Google Slides
            </button>
          </div>

          <div className="import-options">
            <div className="option-row">
              <div className="option-info">
                <StickyNote size={15} className="option-icon" />
                <div>
                  <span className="option-label">Import speaker notes</span>
                  <span className="option-desc">Use slide notes as scene scripts</span>
                </div>
              </div>
              <Switch
                size="small"
                checked={importSpeakerNotes}
                onChange={setImportSpeakerNotes}
                disabled={importing}
              />
            </div>

            <div className="option-row">
              <div className="option-info">
                <Sparkles size={15} className="option-icon" />
                <div>
                  <span className="option-label">
                    Generate AI scripts
                  </span>
                  <span className="option-desc">AI writes narration from slide content</span>
                </div>
              </div>
              <Switch
                size="small"
                checked={generateAIScripts}
                onChange={(checked) => {
                  if (!isPaid && checked) {
                    setShowUpgradePrompt(true);
                    return;
                  }
                  setGenerateAIScripts(checked);
                }}
                disabled={importing}
              />
            </div>
          </div>

          {activeTab === 'upload' ? renderUploadContent() : renderGoogleSlidesContent()}

          {error && (
            <div className="import-error">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}
        </>
      )}
    </Modal>

    <UpgradePrompt
      visible={showUpgradePrompt}
      onClose={() => setShowUpgradePrompt(false)}
      feature="AI Script Generation"
      description="Upgrade to Pro to unlock AI-powered narration scripts generated from your slide content."
    />
    </>
  );
};
